"use client";

import { useEffect, useRef, useMemo, useCallback } from "react";
import { useInView, useMotionValue, useSpring } from "framer-motion";
import { cn } from "@/lib/utils";

export default function NumberTicker({
  value,
  direction = "up",
  delay = 0,
  className,
  decimalPlaces = 0,
}: {
  value: number;
  direction?: "up" | "down";
  className?: string;
  delay?: number; // delay in s
  decimalPlaces?: number;
}) {
  // Use two refs: one for the animated number
  const animatedRef = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(direction === "down" ? value : 0);
  const springValue = useSpring(motionValue, {
    damping: 60,
    stiffness: 100,
  });
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "0px" });

  const numberFormatter = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  }, [decimalPlaces]);

  useEffect(() => {
    if (isInView) {
      const timeout = setTimeout(() => {
        motionValue.set(direction === "down" ? 0 : value);
      }, delay * 1000);
      return () => clearTimeout(timeout);
    }
  }, [motionValue, isInView, delay, value, direction]);

  const handleSpringChange = useCallback(
    (latest: number) => {
      if (animatedRef.current) {
        animatedRef.current.textContent = numberFormatter.format(
          Number(latest.toFixed(decimalPlaces))
        );
      }
    },
    [numberFormatter, decimalPlaces]
  );

  useEffect(() => {
    const unsubscribe = springValue.on("change", handleSpringChange);
    return () => unsubscribe();
  }, [springValue, handleSpringChange]);

  return (
    <span
      ref={ref}
      className={cn(
        "relative inline-block tabular-nums tracking-wider text-black dark:text-white",
        className
      )}
    >
      {/* Invisible placeholder that reserves the final width */}
      <span className="invisible opacity-0">
        {numberFormatter.format(value)}
      </span>
      {/* Animated number overlay */}
      <span className="absolute inset-0" ref={animatedRef} />
    </span>
  );
}
